/**
 * Slider 1D component with labels and values showing.
 *
 * @unstable
 */

import { map, shareReplay } from 'rxjs/operators';
import { combineLatest } from 'rxjs';

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  Input,
  OnChanges,
  OnInit,
  SimpleChange,
  SimpleChanges,
} from '@angular/core';

import { SliderAbstractComponent } from '@player/+survey/question/slider-abstract/slider-abstract.component';

import { Questions } from '@shared/enums/questions.enum';
import { FadeInOut } from '@shared/animations/fade-in-out.anim';
import { QuestionData, SliderValuesData } from '@shared/models/survey.model';
import { AsyncPipe, NgClass, NgStyle } from '@angular/common';
import { SliderLabel } from '@player/shared/components/slider-label/slider-label.component';
import { SliderThumb } from '@player/shared/components/slider-thumb/slider-thumb.component';
import { SliderImages } from '@player/shared/components/slider-images/slider-images.component';
import { SliderLabel as SliderLabelService } from '@player/shared/services/slider-label.service';

@Component({
  selector: 'slider-1d',
  templateUrl: '../slider-abstract/slider-abstract.component.html',
  styleUrls: ['../slider-abstract/slider-abstract.component.scss', './slider-1d.component.scss'],
  providers: [SliderLabelService],
  animations: [FadeInOut],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgClass, AsyncPipe, SliderLabel, SliderThumb, NgStyle, SliderImages],
})
export class Slider1D extends SliderAbstractComponent<string> implements OnInit, OnChanges {
  readonly cdRef = inject(ChangeDetectorRef);

  readonly knobs: number[] = [0];

  protected readonly questionType: Questions = Questions.SLIDER_1D;

  @Input() showInput: boolean = false;

  ngOnInit(): void {
    super.ngOnInit();

    if (
      this.groupData &&
      this.questionData &&
      this.groupData.type === Questions.GROUP_SCORED &&
      !Questions.isNps(this.questionData)
    ) {
      this.groupAnswers = combineLatest([this.ss.normalQuestions, this.ss.answers]).pipe(
        map(([questions, answers]) =>
          questions
            .filter(
              (q: QuestionData) =>
                answers[q.$key] != null &&
                q.$key !== this.questionData.$key &&
                q.type === this.questionData.type &&
                this.groupData.$key === q.group,
            )
            .map((question) => {
              const val = answers[question.$key];

              return {
                no: questions.indexOf(question) + 1,
                val: val ? parseFloat(val) : null,
              };
            }),
        ),
        shareReplay({ refCount: true, bufferSize: 1 }),
      );
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    super.ngOnChanges(changes);

    const dataChanges: SimpleChange = changes.questionData || changes.groupData;

    if (dataChanges) {
      const current: QuestionData = dataChanges.currentValue || null;

      if (current) {
        current.sliderValues = current.sliderValues || ({} as SliderValuesData);
      }

      this.startPercent = (!current || current.sliderValues.initial == null ? 0.5 : current.sliderValues.initial) * 100;

      this.overlapOffset = undefined;

      this.cdRef.detectChanges();
    }
  }

  protected setCurrentValue(currentValue: string): void {
    const { min, max } = this.getMinMaxInitial();

    let value: number = parseFloat(currentValue);

    value = isNaN(value) ? min : Math.max(min, Math.min(max, value));

    this.currentValue = currentValue ? [value] : [];
  }

  protected emitAnswerChange(currentAnswer: number[]): void {
    const [first] = currentAnswer;
    this.answerChange.emit(first != null ? first.toString() : null);

    if (first != null) {
      this.answerReady.emit();
    }
  }
}
